'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function CiltKurulugundaMezoterapi() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/cilt-kurulugunda-mezoterapi-derin-nem-destegi-ve-hucresel-yenilenme';
  const pageTitle = 'Cilt Kuruluğunda Mezoterapi: Derin Nem Desteği ve Hücresel Yenilenme';
  const pageDescription = 'Cilt kuruluğuna karşı mezoterapi nasıl etki eder? Hyaluronik asit, vitamin ve mineral kokteylleriyle ciltte derin nem ve elastikiyet artışı.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Cilt Kuruluğunda Mezoterapi', href: '/tr/blog/cilt-kurulugunda-mezoterapi-derin-nem-destegi-ve-hucresel-yenilenme' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Cilt kuruluğunda mezoterapi uygulaması"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-cyan-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Mezoterapi & Nem
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Cilt Kuruluğunda Mezoterapi: Derin Nem Desteği ve Hücresel Yenilenme
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>20 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Kuru cilt, yalnızca estetik bir sorun değil; aynı zamanda epidermal bariyerin zayıflaması anlamına gelir.
                Soğuk hava, düşük nem, stres, yetersiz beslenme ve yaşlanma ciltte nem kaybına neden olur.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Kremler ve serumlar yüzeyde etkilidir, ancak cildin alt tabakasındaki nem eksikliğini gideremezler.
                İşte bu noktada mezoterapi, cilt kuruluğuna karşı en etkili medikal çözümlerden biridir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Mezoterapi Nedir?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Mezoterapi, cildin orta tabakasına (mezoderm) mikro iğnelerle vitamin, mineral, aminoasit ve hyaluronik asit enjeksiyonudur.
                Amaç; cildi içeriden beslemek, nemlendirmek ve yenilemektir.
              </p>

              <div className="bg-cyan-50 border-l-4 border-cyan-400 p-4 mb-6">
                <p className="text-cyan-800 font-medium">
                  💉 Kısacası: krem yüzeyi nemlendirir, mezoterapi ise hücreyi nemlendirir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Cilt Kuruluğunda Mezoterapinin Etkisi
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-cyan-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Nem dengesi</td>
                      <td className="border border-gray-300 px-4 py-3">Hyaluronik asit suyu 1000 katına kadar tutar, hücreler arası nemi artırır.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Elastikiyet artışı</td>
                      <td className="border border-gray-300 px-4 py-3">Amino asitler kolajen üretimini tetikler.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Cilt bariyer onarımı</td>
                      <td className="border border-gray-300 px-4 py-3">Vitamin A, C, E kompleksi hücre zarını güçlendirir.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Canlılık ve parlaklık</td>
                      <td className="border border-gray-300 px-4 py-3">Artan dolaşım cildin oksijenlenmesini artırır.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <p className="text-blue-800 font-medium">
                  Sonuç olarak cilt daha dolgun, nemli ve canlı görünür.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygulama Nasıl Yapılır?
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside space-y-3 text-green-800">
                  <li>Cilt antiseptik solüsyonla temizlenir.</li>
                  <li>Lokal anestezik krem uygulanır (yaklaşık 20 dakika beklenir).</li>
                  <li>Mikro iğnelerle özel mezokokteyl cilt altına enjekte edilir.</li>
                  <li>İşlem sonrası yatıştırıcı maske ve nem serumu uygulanır.</li>
                </ol>
                <p className="text-green-800 mt-4 font-medium">
                  Toplam süre: yaklaşık 30–40 dakika.
                </p>
                <p className="text-green-800">
                  Etkiler 3–5 gün içinde fark edilir, tam sonuç 2. seans sonrası belirginleşir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Kullanılan İçerikler
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Cilt kuruluğu için mezoterapide en sık kullanılan aktif maddeler:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">Hyaluronik asit (HA)</h4>
                  <p className="text-purple-800 text-sm">nem tutucu</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">Vitamin C</h4>
                  <p className="text-purple-800 text-sm">antioksidan, aydınlatıcı</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">Vitamin B5 (Pantotenik asit)</h4>
                  <p className="text-purple-800 text-sm">nem bariyerini onarıcı</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">Aminoasit kompleksi</h4>
                  <p className="text-purple-800 text-sm">elastin sentezini uyarıcı</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 md:col-span-2">
                  <h4 className="font-semibold text-purple-900 mb-2">DMAE + Nucleofill türevleri</h4>
                  <p className="text-purple-800 text-sm">sıkılaştırıcı ve hücre yenileyici</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Seans Sıklığı
              </h2>
              
              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <div className="flex items-center mb-4">
                  <span className="text-yellow-600 text-2xl mr-3">📅</span>
                  <span className="text-yellow-800 font-medium">İlk 3 seans: 2–3 hafta aralıklarla</span>
                </div>
                <div className="flex items-center mb-4">
                  <span className="text-yellow-600 text-2xl mr-3">📅</span>
                  <span className="text-yellow-800 font-medium">Sonrasında: 2–3 ayda bir koruma seansı</span>
                </div>
                <p className="text-yellow-800">
                  Kuru cilt yapısına sahip kişilerde yılda 3–4 kür düzenli uygulama önerilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Kimler İçin Uygun?
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 rounded-xl p-6">
                  <h4 className="font-semibold text-green-900 mb-4 flex items-center">
                    <span className="text-green-600 mr-2">✅</span>
                    Uygun Olanlar
                  </h4>
                  <ul className="space-y-2 text-green-800">
                    <li>• Kuru ve nemsiz cilt tipleri</li>
                    <li>• Sigara içen, stresli kişiler</li>
                    <li>• Cildi mat, cansız görünen bireyler</li>
                    <li>• 25 yaş üzeri herkes (koruyucu bakım amaçlı)</li>
                  </ul>
                </div>
                
                <div className="bg-red-50 rounded-xl p-6">
                  <h4 className="font-semibold text-red-900 mb-4 flex items-center">
                    <span className="text-red-600 mr-2">⚠️</span>
                    Uygun Olmayanlar
                  </h4>
                  <ul className="space-y-2 text-red-800">
                    <li>• Hamileler</li>
                    <li>• Emziren anneler</li>
                    <li>• Enfeksiyon veya aktif akne sorunu olanlar</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Yan Etkiler ve Sonrası Bakım
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-orange-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Yan Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Süre</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif kızarıklık</td>
                      <td className="border border-gray-300 px-4 py-3">1–3 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Normal cilt tepkisidir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif ödem</td>
                      <td className="border border-gray-300 px-4 py-3">6–12 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Soğuk kompresle azalır</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Minik morluk</td>
                      <td className="border border-gray-300 px-4 py-3">2–4 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Hassas ciltlerde görülebilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kaşıntı</td>
                      <td className="border border-gray-300 px-4 py-3">1–2 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Hafif, geçici</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-blue-900 mb-4">Sonrası Bakım Kuralları:</h4>
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>İlk 24 saat makyaj yapılmamalı</li>
                  <li>48 saat sauna ve sıcak duştan kaçınılmalı</li>
                  <li>İlk hafta güneş koruyucu kullanılmalı (SPF 30+)</li>
                  <li>Bol su içilmeli (günde 2-3 litre)</li>
                  <li>İlk 3 gün cilt masajı yapılmamalı</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8. Fiyat Bilgisi
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="text-center">
                    <h4 className="font-semibold text-green-900 mb-2">Tek Seans</h4>
                    <p className="text-2xl font-bold text-green-800">₺2.500 - ₺3.500</p>
                    <p className="text-green-700 text-sm">Kokteyl içeriğine göre değişir</p>
                  </div>
                  <div className="text-center">
                    <h4 className="font-semibold text-green-900 mb-2">3 Seans Paket</h4>
                    <p className="text-2xl font-bold text-green-800">₺6.500 - ₺9.000</p>
                    <p className="text-green-700 text-sm">%15 indirimli</p>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <p className="text-green-800 text-sm">
                    * Fiyatlar 2026 yılı için geçerlidir. Detaylı fiyat bilgisi için iletişime geçin.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9. Sonuç ve Kalıcılık
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                İlk seanstan sonra ciltte parlaklık ve dolgunluk hemen hissedilir.
                Kalıcılık süresi 4–6 ay, düzenli uygulamalarda 1 yıla kadar uzar.
              </p>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-purple-900 mb-4">Beklenen Sonuçlar:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-purple-600 mr-2">✅</span>
                      <span className="text-purple-800">%85 nem artışı (1. seans sonrası)</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-purple-600 mr-2">✅</span>
                      <span className="text-purple-800">%60 elastikiyet artışı (3. seans sonrası)</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-purple-600 mr-2">✅</span>
                      <span className="text-purple-800">%90 hasta memnuniyeti</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-purple-600 mr-2">✅</span>
                      <span className="text-purple-800">%0.1 yan etki oranı</span>
                    </div>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                10. Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Cilt kuruluğu, sadece estetik bir sorun değil; aynı zamanda cildin sağlığının bozulduğunun göstergesidir. 
                  Mezoterapi ile cildin alt tabakalarına ulaşarak, hücresel düzeyde nem ve beslenme sağlıyoruz. 
                  Bu sayede cilt sadece dışarıdan değil, içeriden de güçleniyor ve doğal parlaklığını geri kazanıyor."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Mezoterapi cildi kalınlaştırır mı?</h3>
                  <p className="text-gray-600">Hayır. Sadece nem ve elastin desteği sağlar, cilt dokusu daha canlı görünür.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Kışın da yapılabilir mi?</h3>
                  <p className="text-gray-600">Evet, hatta en etkili dönemdir. Soğuk hava cildin nem ihtiyacını artırır.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Yan etki olur mu?</h3>
                  <p className="text-gray-600">Geçici kızarıklık ve hafif ödem olabilir, birkaç saat içinde geçer.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Ne kadar süre etkili olur?</h3>
                  <p className="text-gray-600">4-6 ay kalıcılık sağlar, düzenli uygulamalarda 1 yıla kadar uzar.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Ağrılı bir işlem midir?</h3>
                  <p className="text-gray-600">Hayır. Anestezik kremle birlikte rahatsızlık hissi minimumdur.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Hangi yaşlarda yapılabilir?</h3>
                  <p className="text-gray-600">25 yaş üzeri herkese uygulanabilir. Yaş ilerledikçe etkinlik artar.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Harvard Health (2024) – "Mesotherapy and Skin Hydration"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Nemlendirme Amaçlı Mezoterapi Rehberi"</li>
                  <li>ISAPS Global Report (2024) – "Mesotherapy for Dehydrated Skin"</li>
                  <li>Mayo Clinic (2023) – "Hyaluronic Acid and Skin Barrier Studies"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#mezoterapi</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#ciltkuruluğu</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#nem</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#ciltbakımı</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#estetik</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-cyan-600 to-blue-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Cilt Kuruluğu İçin Mezoterapi Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan cilt kuruluğu ve mezoterapi tedavisi hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Cilt kuruluğu için mezoterapi hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-cyan-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/mezoterapi-etkileri-ne-zaman-gorulur"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Mezoterapinin Etkileri Ne Zaman Görülür?</h4>
              <p className="text-gray-600 text-sm">Mezoterapi uygulamasından sonra etkilerin ne zaman görülmeye başladığı hakkında bilgiler.</p>
            </a>
            <a
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir, Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama süreci hakkında detaylı bilgiler.</p>
            </a>
            <a
              href="/tr/blog/orijinal-dolgu-anlama-yontemleri-sahte-urunlerden-korunma"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Orijinal Dolguyu Anlama Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Orijinal dolgu nasıl anlaşılır ve sahte ürünlerden nasıl korunulur?</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-cyan-600 text-white rounded-full hover:bg-cyan-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
